/**
 * Centralized timeout configuration for viewer operations.
 * These timeouts prevent various viewer operations from hanging indefinitely.
 *
 * Override all timeouts via URL query param: ?viewerTimeout=5000
 * (reads from parent window URL since viewer runs in iframe)
 *
 * Dynamically change timeouts from console:
 *   window.__viewerTimeouts__.PAGE_READY_TIMEOUT_MS = 5000
 *   window.__viewerTimeouts__.ALL = 3000  // override all at once
 */

const DEFAULT_TIMEOUTS = {
	PAGE_READY_TIMEOUT_MS: 25000,
	REFRESH_APPS_TIMEOUT_MS: 25000,
	WAIT_FOR_VIEWER_TIMEOUT_MS: 20000,
	LOAD_CONTROLLER_MODULES_TIMEOUT_MS: 25000,
	WAIT_FOR_SDKS_TIMEOUT_MS: 25000,
	CREATE_CONTROLLERS_TIMEOUT_MS: 25000,
	FLUSH_ON_READY_TIMEOUT_MS: 25000,
}

const getTimeoutOverride = (): number | null => {
	try {
		if (typeof window === 'undefined') {
			return null
		}
		// Try parent window first (editor iframe scenario), fall back to current window
		const url = window.parent?.location?.href || window.location?.href
		if (!url) {
			return null
		}
		const params = new URL(url).searchParams
		const override = params.get('viewerTimeout')
		if (override) {
			const value = parseInt(override, 10)
			if (!isNaN(value) && value > 0) {
				console.log(`[timeoutConfig] Using viewerTimeout override: ${value}ms`)
				return value
			}
		}
	} catch {
		// Cross-origin or other error - ignore
	}
	return null
}

const urlOverride = getTimeoutOverride()

// Mutable timeout values that can be changed from console
const mutableTimeouts = {
	ALL: urlOverride, // Global override for all timeouts
	PAGE_READY_TIMEOUT_MS: urlOverride ?? DEFAULT_TIMEOUTS.PAGE_READY_TIMEOUT_MS,
	REFRESH_APPS_TIMEOUT_MS: urlOverride ?? DEFAULT_TIMEOUTS.REFRESH_APPS_TIMEOUT_MS,
	WAIT_FOR_VIEWER_TIMEOUT_MS: urlOverride ?? DEFAULT_TIMEOUTS.WAIT_FOR_VIEWER_TIMEOUT_MS,
	LOAD_CONTROLLER_MODULES_TIMEOUT_MS: urlOverride ?? DEFAULT_TIMEOUTS.LOAD_CONTROLLER_MODULES_TIMEOUT_MS,
	WAIT_FOR_SDKS_TIMEOUT_MS: urlOverride ?? DEFAULT_TIMEOUTS.WAIT_FOR_SDKS_TIMEOUT_MS,
	CREATE_CONTROLLERS_TIMEOUT_MS: urlOverride ?? DEFAULT_TIMEOUTS.CREATE_CONTROLLERS_TIMEOUT_MS,
	FLUSH_ON_READY_TIMEOUT_MS: urlOverride ?? DEFAULT_TIMEOUTS.FLUSH_ON_READY_TIMEOUT_MS,
}

// Expose on window for console access
if (typeof window !== 'undefined') {
	;(window as any).__viewerTimeouts__ = mutableTimeouts
}

export const timeoutConfig = {
	/** Timeout for individual controller pageReady calls (ms) */
	get PAGE_READY_TIMEOUT_MS() {
		return mutableTimeouts.ALL ?? mutableTimeouts.PAGE_READY_TIMEOUT_MS
	},

	/** Timeout for refreshAppsInCurrentPage operation (ms) */
	get REFRESH_APPS_TIMEOUT_MS() {
		return mutableTimeouts.ALL ?? mutableTimeouts.REFRESH_APPS_TIMEOUT_MS
	},

	/** Hard timeout for waitForViewer to ensure it never hangs indefinitely (ms) */
	get WAIT_FOR_VIEWER_TIMEOUT_MS() {
		return mutableTimeouts.ALL ?? mutableTimeouts.WAIT_FOR_VIEWER_TIMEOUT_MS
	},

	/** Timeout for loading controller modules (ms) */
	get LOAD_CONTROLLER_MODULES_TIMEOUT_MS() {
		return mutableTimeouts.ALL ?? mutableTimeouts.LOAD_CONTROLLER_MODULES_TIMEOUT_MS
	},

	/** Timeout for waiting for component SDKs to load (ms) */
	get WAIT_FOR_SDKS_TIMEOUT_MS() {
		return mutableTimeouts.ALL ?? mutableTimeouts.WAIT_FOR_SDKS_TIMEOUT_MS
	},

	/** Timeout for creating controllers via appModule.createControllers (ms) */
	get CREATE_CONTROLLERS_TIMEOUT_MS() {
		return mutableTimeouts.ALL ?? mutableTimeouts.CREATE_CONTROLLERS_TIMEOUT_MS
	},

	/** Timeout for flushing onReady callbacks (ms) */
	get FLUSH_ON_READY_TIMEOUT_MS() {
		return mutableTimeouts.ALL ?? mutableTimeouts.FLUSH_ON_READY_TIMEOUT_MS
	},
}
